/**
 * src/services/spatial/spatialTreasuryClient.ts
 * Reading and funding a spatial object's treasury.
 *
 * Same conventions as `spatialPresenceClient`: a `PresenceResult`, never
 * a thrown error, because every caller here is a card on a map that has
 * to render a refusal rather than disappear.
 *
 * Nothing in this module signs anything. It fetches an unsigned envelope
 * and hands it to whichever signer the caller has — Freighter for a
 * G-address, the passkey service for a smart account — then submits what
 * comes back. The fork is here rather than in each component because
 * there are now three places that need it and one of them is a hover
 * card.
 */

import { V2_API } from '../../v2/config';
import type { PresenceResult } from './spatialPresenceClient';

/** An amount and the asset it is an amount of. Never one without the other. */
export interface TreasuryAsset {
  address: string;
  code: string | null;
  decimals: number;
  isNative: boolean;
}

export interface TreasuryBalance {
  asset: TreasuryAsset | null;
  /** Whole stroops, as a string: an i128 does not fit in a number. */
  stroops: string;
  /** The same value, decimal, for display. */
  amount: string;
}

/** Why an object cannot be funded right now, in the server's vocabulary. */
export type TreasuryBlocker =
  | 'registry_not_configured'
  | 'object_not_registered'
  | 'object_suspended'
  | 'object_retired'
  | 'organization_cannot_receive';

export interface TreasuryStatus {
  objectId: string;
  objectUuid: string | null;
  /** The 32-byte scope the balance is stored under, as hex. */
  scopeId: string;
  asset: TreasuryAsset | null;
  balance: TreasuryBalance;
  /** The wallet entitled to withdraw, or null when nobody is named. */
  beneficiary: string | null;
  canReceive: boolean;
  blocker: TreasuryBlocker | null;
  registry: {
    configured: boolean;
    address: string | null;
    expected: string | null;
    matchesExpected: boolean;
  };
  objectStatus: number | null;
  country: number | null;
  cached: boolean;
}

export interface BuiltTreasuryTx {
  objectId: string;
  objectUuid: string | null;
  scopeId: string;
  asset: TreasuryAsset | null;
  amountStroops: string;
  amount: string;
  xdr: string;
  latestLedger: number;
  /**
   * True when the service account sourced the envelope for a smart
   * account. Handed straight back on submit; the server adds its own
   * signature only for a transaction it sourced.
   */
  sponsored: boolean;
}

// ── Plumbing ─────────────────────────────────────────────────────────────────

const BASE = `${V2_API}/spatial-treasury`;

async function call<T>(path: string, init?: RequestInit): Promise<PresenceResult<T>> {
  let resp: Response;
  try {
    resp = await fetch(`${BASE}${path}`, init);
  } catch (err) {
    return { ok: false, reason: 'network_error', message: (err as Error).message };
  }

  let body: any = null;
  try { body = await resp.json(); } catch { /* empty body */ }

  // The V2 routes answer `success` and `error` where the presence routes
  // answer `ok` and `reason`. Normalized here so one refusal renderer and
  // one success check serve both families.
  if (!resp.ok || body?.success === false) {
    return {
      ...(body || {}),
      ok: false,
      reason: body?.reason || body?.error || `http_${resp.status}`,
    } as PresenceResult<T>;
  }
  return { ...(body || {}), ok: true } as PresenceResult<T>;
}

function postJson<T>(path: string, body: unknown): Promise<PresenceResult<T>> {
  return call<T>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
}

// ── Amounts ──────────────────────────────────────────────────────────────────

/** Stroops per unit, matching the server's `NATIVE_DECIMALS`. */
export const TREASURY_DECIMALS = 7;

/**
 * A typed decimal amount as whole stroops, or null.
 *
 * Digits are moved, never multiplied: `Math.round(value * 1e7)` is
 * wrong for values a person will actually type, and a deposit is the
 * wrong place to discover it. Shifting the point is exact for every
 * input, needs no BigInt — which this bundle targets too low to write
 * literals for — and gives the same answer as `parseUnits` in
 * server/v2/lib/assets.js, which is the number the contract will see.
 */
export function toStroops(amount: string, decimals = TREASURY_DECIMALS): string | null {
  const raw = String(amount ?? '').trim();
  if (!/^\d*(\.\d*)?$/.test(raw) || raw === '' || raw === '.') return null;

  const [whole, fraction = ''] = raw.split('.');
  if (fraction.length > decimals) return null;

  const digits = `${whole || '0'}${fraction.padEnd(decimals, '0')}`.replace(/^0+(?=\d)/, '');

  return /[1-9]/.test(digits) ? digits : null;
}

/** Whole stroops as a decimal string. Mirrors the server's `formatUnits`. */
export function fromStroops(stroops: string | number, decimals = TREASURY_DECIMALS): string {
  const raw = String(stroops ?? '0').trim();
  if (!/^-?\d+$/.test(raw)) return '0';

  const negative = raw.startsWith('-');
  const digits = (negative ? raw.slice(1) : raw).padStart(decimals + 1, '0');

  const whole = digits.slice(0, digits.length - decimals).replace(/^0+(?=\d)/, '');
  const fraction = decimals > 0
    ? digits.slice(digits.length - decimals).replace(/0+$/, '')
    : '';

  const sign = negative && /[1-9]/.test(digits) ? '-' : '';
  return `${sign}${whole}${fraction ? `.${fraction}` : ''}`;
}

/** How an amount reads on screen, with its unit attached. */
export function describeBalance(balance: TreasuryBalance | null | undefined): string {
  if (!balance) return '—';
  const code = balance.asset?.code
    || (balance.asset?.address ? `${balance.asset.address.slice(0, 4)}…` : '');
  return code ? `${balance.amount} ${code}` : balance.amount;
}

/**
 * Whether this deposit can be attempted, and why not.
 *
 * Pure, so the button's enabled state can be tested without a map, a
 * wallet or a network. It is a courtesy check only: the contract refuses
 * on its own terms, and hiding a button is not authorization.
 */
export function checkDeposit(
  amount: string,
  status: TreasuryStatus | null,
  wallet: string | null | undefined,
): { ok: true; stroops: string } | { ok: false; reason: string } {
  if (!wallet) return { ok: false, reason: 'no_wallet' };
  // Unknown is not the same as refused: a status that has not arrived yet
  // should leave the button waiting rather than claim a refusal.
  if (!status) return { ok: false, reason: 'status_unknown' };
  if (!status.canReceive) return { ok: false, reason: status.blocker || 'organization_cannot_receive' };

  const stroops = toStroops(amount, status.asset?.decimals ?? TREASURY_DECIMALS);
  if (!stroops) return { ok: false, reason: 'bad_amount' };

  return { ok: true, stroops };
}

// ── Reads ────────────────────────────────────────────────────────────────────

/**
 * One object's treasury status.
 *
 * Deliberately not called on hover. Hovering stays free of the network;
 * this is what a pinned selection fetches.
 */
export function getObjectTreasury(
  objectId: string,
  asset?: string | null,
): Promise<PresenceResult<TreasuryStatus>> {
  const query = asset ? `?asset=${encodeURIComponent(asset)}` : '';
  return call<TreasuryStatus>(`/${encodeURIComponent(objectId)}/status${query}`);
}

// ── Writes ───────────────────────────────────────────────────────────────────

export function buildObjectDeposit(input: {
  objectId: string;
  depositor: string;
  amountStroops: string;
  asset?: string | null;
}): Promise<PresenceResult<BuiltTreasuryTx>> {
  return postJson<BuiltTreasuryTx>(`/${encodeURIComponent(input.objectId)}/deposit-xdr`, {
    depositor: input.depositor,
    amountStroops: input.amountStroops,
    asset: input.asset ?? undefined,
  });
}

export function buildObjectWithdrawal(input: {
  objectId: string;
  beneficiary: string;
  amountStroops: string;
  asset?: string | null;
}): Promise<PresenceResult<BuiltTreasuryTx>> {
  return postJson<BuiltTreasuryTx>(`/${encodeURIComponent(input.objectId)}/withdraw-xdr`, {
    beneficiary: input.beneficiary,
    amountStroops: input.amountStroops,
    asset: input.asset ?? undefined,
  });
}

export function submitObjectTreasuryTx(input: {
  signedXdr: string;
  objectId?: string;
  asset?: string | null;
  sponsored?: boolean;
}): Promise<PresenceResult<{ hash: string; ledger?: number }>> {
  return postJson('/submit', input);
}

// ── Signing ──────────────────────────────────────────────────────────────────

/**
 * Whoever can turn an unsigned envelope into a signed one.
 *
 * An interface rather than a concrete wallet so the deposit card does not
 * have to know whether it is talking to a browser extension or a
 * passkey, and so a test can sign without either.
 */
export interface TreasurySigner {
  signTransaction(xdr: string, latestLedger?: number): Promise<string>;
}

/** A connected browser wallet, signing for its own G-address. */
export function classicTreasurySigner(
  wallet: { signTransaction(xdr: string, networkPassphrase?: string): Promise<string> },
  networkPassphrase?: string,
): TreasurySigner {
  return { signTransaction: (xdr) => wallet.signTransaction(xdr, networkPassphrase) };
}

/** A passkey smart account, authorizing an envelope the service sourced. */
export function passkeyTreasurySigner<A>(
  service: { signUnsignedXdr(xdr: string, account: A, latestLedger?: number): Promise<string> },
  account: A,
): TreasurySigner {
  return {
    signTransaction: (xdr, latestLedger) => service.signUnsignedXdr(xdr, account, latestLedger),
  };
}

/**
 * Build, sign and submit in one call.
 *
 * Returns the refusal from whichever step refused, so a card can say
 * "the object cannot receive right now" and "you declined the signature"
 * differently. A signer that throws — which is what every wallet does
 * when a person cancels — becomes `signature_declined` rather than an
 * unhandled rejection.
 */
export async function depositToObject(input: {
  objectId: string;
  depositor: string;
  amountStroops: string;
  asset?: string | null;
  signer: TreasurySigner;
}): Promise<PresenceResult<{ hash: string; ledger?: number; amount: string }>> {
  const built = await buildObjectDeposit(input);
  if (!built.ok) return built;

  let signedXdr: string;
  try {
    signedXdr = await input.signer.signTransaction(built.xdr, built.latestLedger);
  } catch (err) {
    return { ok: false, reason: 'signature_declined', message: (err as Error).message };
  }

  const sent = await submitObjectTreasuryTx({
    signedXdr,
    objectId: input.objectId,
    asset: built.asset?.address ?? input.asset ?? undefined,
    sponsored: built.sponsored,
  });
  if (!sent.ok) return sent;

  return { ok: true, hash: sent.hash, ledger: sent.ledger, amount: built.amount };
}

/** The same three steps for a beneficiary collecting what an object holds. */
export async function withdrawFromObject(input: {
  objectId: string;
  beneficiary: string;
  amountStroops: string;
  asset?: string | null;
  signer: TreasurySigner;
}): Promise<PresenceResult<{ hash: string; ledger?: number; amount: string }>> {
  const built = await buildObjectWithdrawal(input);
  if (!built.ok) return built;

  let signedXdr: string;
  try {
    signedXdr = await input.signer.signTransaction(built.xdr, built.latestLedger);
  } catch (err) {
    return { ok: false, reason: 'signature_declined', message: (err as Error).message };
  }

  const sent = await submitObjectTreasuryTx({
    signedXdr,
    objectId: input.objectId,
    asset: built.asset?.address ?? input.asset ?? undefined,
    sponsored: built.sponsored,
  });
  if (!sent.ok) return sent;

  return { ok: true, hash: sent.hash, ledger: sent.ledger, amount: built.amount };
}

// ── Wording ──────────────────────────────────────────────────────────────────

const BLOCKER_TEXT: Record<string, string> = {
  no_wallet: 'Connect a wallet to contribute.',
  status_unknown: 'Checking what this object can accept…',
  bad_amount: 'Enter an amount with up to seven decimal places.',
  registry_not_configured: 'Object treasuries are not switched on for this deployment yet.',
  object_not_registered: 'This object is not registered on chain, so it cannot hold value.',
  object_suspended: 'This object is suspended and cannot accept contributions.',
  object_retired: 'This object has been retired.',
  organization_cannot_receive: 'The organization behind this object is not cleared to receive funds.',
  signature_declined: 'The signature was declined.',
  network_error: 'Could not reach the network. Try again.',
};

/** Why a deposit is not on offer, in a sentence a visitor can act on. */
export function treasuryBlockerText(reason: string | null | undefined): string | null {
  if (!reason) return null;
  return BLOCKER_TEXT[reason] || 'That did not go through.';
}
